<template>
  <b-popover
    :target="target"
    delay="500"
    triggers="hover"
    placement="bottom"
    custom-class="kr-value-popover"
    ref="popover"
    @hide="beforeHide"
    @hidden="onHide"
    @shown="onShow"
  >
    <div class="content" :class="{ ['edit-view']: showKrValuesEditor }">
      <div v-if="showKrValuesEditor">
        <kr-values-editor :errors="editErrors" :kr="kr" @change="onKrValuesChange" />
        <div class="field-error-message mt-3" v-if="editErrors.message">
          {{ editErrors.message }}
        </div>
        <submit-buttons class="mt-3" @save="onSubmit" @cancel="onCancel" />
      </div>
      <div v-else>
        <div class="section flex-gap-14 d-flex">
          <div>
            <div class="label">Start and Target</div>
            <div class="text-medium-1">
              {{ measurementText(kr.start) }}
              <i class="arrow-right-icon"></i>
              {{ measurementText(kr.end) }}
            </div>
          </div>
          <div v-if="kr.isClosed">
            <div class="label">Outcome</div>
            <span class="variant bold" :data-variant="colorVariant" data-apply-color>
              {{ getOutcomeLabel(kr.outcome) }}
            </span>
          </div>
          <div v-else>
            <div class="label">Status</div>
            <div>
              <span class="kr-status variant py-1" :data-variant="colorVariant" data-apply-bg-color>
                {{ krStatusText(kr.status) }}
              </span>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="label">Check-ins history</div>
          <check-in-history-list
            :kr="kr"
            :loading-state="checkInLoadingState"
            :checkin-history="checkinHistory"
            @fetch="fetchCheckInHistory"
            :is-visible="popoverShown"
          />
        </div>
        <div class="p-1 text-medium-1">
          KR Contribution:
          <link-button class="p-0 text-medium-1" link-color="brand" @click="showContributionModal"
            >{{ formatWeightage(kr.weightage) }}
          </link-button>
        </div>
        <b-button
          v-if="kr.id"
          variant="link"
          class="pl-1 pb-0 text-medium-1"
          @click="showValueEditor"
          >Edit KR Values
        </b-button>
        <setup-goal-progress-modal
          :goal-id="goal.id"
          :kr-to-edit="this.kr.id"
          :source="SOURCE.KR_DETAILS_POPOVER"
          v-model="isContributionModalShown"
          v-if="isContributionModalShown"
        />
      </div>
    </div>
  </b-popover>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton'
import SetupGoalProgressModal from '@/components/goal/SetupGoalProgressModal'
import CheckInHistoryList from '@/components/kr/CheckInHistoryList.vue'
import { SOURCE } from '@/constants/analytics'
import { LoadingState, VARIANT_MAP } from '@/constants/common'
import events from '@/constants/events'
import { FLEXIBLE_WEIGHTAGE } from '@/constants/goal'
import { DEFAULT_ERROR } from '@/constants/messages'

/**
 * This component show kr details in popover
 * It also facilitates editing of KR values (measureType, start, end, weightage)
 */
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import ParentGoalsMixin from '@/mixins/ParentGoalsMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import getOutcomeLabel from '@/utils/getOutcomeLabel'
import toPercentage from '@/utils/toPercentage'
import * as R from 'ramda'
import { BPopover, BButton } from 'bootstrap-vue'
import KrValuesEditor from '@/components/KrValuesEditor'
import SubmitButtons from '@/components/SubmitButtons'
import { formatHyphenatedString, krMeasurementText } from '@/goalUtils'
import { mapActions } from 'vuex'

export default {
  name: 'KrDetailsPopover',
  props: ['target', 'kr'],
  mixins: [ParentGoalsMixin, EventBusMixin],
  components: {
    CheckInHistoryList,
    BPopover,
    BButton,
    KrValuesEditor,
    SubmitButtons,
    LinkButton,
    SetupGoalProgressModal
  },
  data() {
    return {
      showKrValuesEditor: false,
      changedKr: this.kr,
      editErrors: {},
      isContributionModalShown: false,
      SOURCE,
      popoverShown: false,
      checkInLoadingState: LoadingState.WAITING,
      checkinHistory: null
    }
  },
  computed: {
    colorVariant() {
      return VARIANT_MAP['outcome-' + this.kr.outcome] || VARIANT_MAP['status-' + this.kr.status]
    },
    goal() {
      return this.$store.getters.goalById(this.kr.goalId)
    }
  },
  methods: {
    ...mapActions(['patchKR', 'getGoal']),
    formatWeightage(number) {
      if (number === FLEXIBLE_WEIGHTAGE) {
        return 'Flexible'
      } else {
        return toPercentage(number)
      }
    },
    krStatusText: formatHyphenatedString,
    measurementText(value) {
      return krMeasurementText(this.kr.measureBy, value)
    },
    onKrValuesChange(kr) {
      this.changedKr = kr
    },
    onCancel() {
      this.showKrValuesEditor = false
      this.changedKr = this.kr
      this.editErrors = {}
    },
    async onSubmit() {
      try {
        await this.patchKR(this.changedKr)
        this.showKrValuesEditor = false
        this.$refs.popover.$emit('close')
        this.getGoal({
          goalId: this.kr.goalId,
          orgId: this.kr.organizationId,
          options: {
            expand: 'parent_goal_ids'
          }
        })
        this.refreshParentGoals(this.kr.goalId)
        EventBus.$emit('ADD_TOAST', { message: 'Changes have been saved successfully' })
        this.editErrors = {}
      } catch (e) {
        const response = R.pathOr({}, ['response', 'data'], e)
        if (response.detail) {
          this.editErrors = response.detail
        } else {
          this.editErrors = { message: response.message || DEFAULT_ERROR }
        }
      }
    },
    showValueEditor() {
      this.showKrValuesEditor = true
      this.changedKr = this.kr
    },
    onKBSubmit() {
      if (this.showKrValuesEditor) {
        this.onSubmit()
      }
    },
    onHide() {
      this.onCancel()
      this.popoverShown = false
      EventBus.$off('SUBMIT_FORM', this.onKBSubmit)
    },
    onShow() {
      this.popoverShown = true
      EventBus.$on('SUBMIT_FORM', this.onKBSubmit)
    },
    getOutcomeLabel,
    beforeHide(e) {
      if (this.showKrValuesEditor || this.isContributionModalShown) {
        e.preventDefault()
      }
    },
    showContributionModal() {
      this.isContributionModalShown = true
    },
    async fetchCheckInHistory() {
      this.checkInLoadingState = LoadingState.LOADING
      try {
        const { data } = await service.client.get(
          urlManager.krEvents({
            orgId: this.kr.organizationId,
            krId: this.kr.id
          }),
          {
            params: {
              type: 'checkin'
            }
          }
        )
        this.checkinHistory = R.reverse(data)
        this.checkInLoadingState = LoadingState.LOADED
      } catch (e) {
        this.checkInLoadingState = LoadingState.ERRORED
      }
    }
  },
  mounted() {
    this.listenToEventBus(events.CHECKIN_POSTED, event => {
      if (event.keyResultId === this.kr.id) this.checkinHistory = null
    })
  },
  watch: {
    popoverShown(popoverShown) {
      if (popoverShown && !this.checkinHistory) this.fetchCheckInHistory()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/goals';
@import '~@/styles/errors';

.kr-value-popover {
  @include scrollbarWidth($scrollSecondary);
  font-size: 0.875rem;
  z-index: 1034;
  max-height: 60vh;
  overflow: auto;
}

.kr-status {
  margin-bottom: 8px;
  display: inline-block;
}

.content {
  min-width: 150px;
}

.content.edit-view {
  min-width: 260px;
}

.section {
  padding-bottom: 14px;
  margin-bottom: 12px;
  border-bottom: 1px solid $borderPrimary;
}

.label {
  font-size: $fontSizeS;
  margin-bottom: 8px;
}

.arrow-right-icon {
  height: 10px;
}
</style>
